import { useTranslation } from 'react-i18next';
import { CaretDown, Check } from '@phosphor-icons/react';
import { useAuth } from '@/contexts/AuthContext';
import { TIERS, tierNameKey, type Tier } from '@/lib/tiers';
import { TierIcon } from '@/components/ui/tier-icon';
import { cn } from '@/lib/utils';
import { inSpeedRun } from '@/lib/speedRun';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** Badge colour per tier, so the lens in use is never mistaken for Admin. */
const TONE: Record<Tier, string> = {
  free: 'bg-muted text-foreground border-border',
  telegram: 'bg-sky-500/15 text-sky-300 border-sky-500/40',
  regular: 'bg-primary/15 text-primary border-primary/40',
  premium: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  privileged: 'bg-violet-500/15 text-violet-300 border-violet-500/40',
  admin: 'bg-destructive text-destructive-foreground border-transparent',
};

/****************
 * THE ADMIN BADGE, AS A SWITCH. Guillaume, 2026-09-26: "Next to my Email, you
 * can now see 'Admin' but I should be able to click on it and change it for
 * any of the 5 options and see what this level of user would see."
 *
 * Admins only. Picking a tier sets the lens (contexts/AuthContext.tsx); every
 * locked feature then shows as that tier sees it, and a stripe says so on
 * every screen. Picking Admin takes the lens off.
 ****************/
export function TierLensSwitch() {
  const { t } = useTranslation('common');
  const { isRealAdmin, tierLens, setTierLens } = useAuth();
  if (!isRealAdmin) return null;
  const current: Tier = tierLens ?? 'admin';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="tier-lens"
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-semibold transition-colors',
            TONE[current],
          )}
          title={t('tier.lens.title')}
        >
          {current !== 'admin' && <TierIcon tier={current} size={11} className="text-current" />}
          {t(tierNameKey(current))}
          <CaretDown size={10} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
          {t('tier.lens.title')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {TIERS.map((tier) => (
          <DropdownMenuItem
            key={tier}
            onClick={() => setTierLens(tier === 'admin' ? null : tier)}
            className="flex items-start gap-2 cursor-pointer"
          >
            <Check size={14} className={cn('mt-0.5 shrink-0', tier === current ? 'opacity-100' : 'opacity-0')} />
            <div className="min-w-0">
              <p className="text-sm font-medium flex items-center gap-1.5"><TierIcon tier={tier} size={13} />{t(tierNameKey(tier))}</p>
              <p className="text-[11px] text-muted-foreground leading-snug">{t(`tier.summary.${tier}`)}</p>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Said on every screen while a lens is on, with the way back. */
export function TierLensStripe() {
  const { t } = useTranslation('common');
  const { tierLens, setTierLens } = useAuth();
  // During a speed run the lens is Free, but the way out is the side menu's
  // "Leave speed run" (Guillaume 11-01: no "Back to Admin" stripe).
  if (!tierLens || tierLens === 'admin' || inSpeedRun()) return null;
  return (
    <div className="fixed inset-x-0 top-0 z-[100] bg-primary text-primary-foreground text-[11px] font-medium text-center py-1 px-3">
      {t('tier.lens.stripe', { tier: t(tierNameKey(tierLens)) })}{' '}
      <button type="button" onClick={() => setTierLens(null)} className="underline underline-offset-2 ms-1">
        {t('tier.lens.back')}
      </button>
    </div>
  );
}
